import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import { sobProposalFormSchema, type SobProposalFormValues } from "../model/schemas";
import { getStationName } from "../model/utils";

import { StyledTableCell, StyledTableHead } from "./styled-table-components";

import type { OrderDetailResponseDTO } from "@/common/api";
import { UnloadingDirectionDTOTariffRoad, useGetMyOffers, useSaveMyOffers } from "@/common/api";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Table,
  TableBody,
  TableHeader,
  TableRow,
} from "@/common/components";
import { useAuth, useToast } from "@/common/hooks";
import { TARIFF_LABELS } from "@/common/model";

interface SobRebiddingFormProps {
  order: OrderDetailResponseDTO;
}

export const SobRebiddingForm = ({ order }: SobRebiddingFormProps) => {
  const { toast } = useToast();
  const { auth } = useAuth();

  const form = useForm<SobProposalFormValues>({
    resolver: zodResolver(sobProposalFormSchema),
    mode: "onChange",
    shouldUnregister: false,
    defaultValues: {
      proposals: {},
      tariffRoad: {},
    },
  });

  const {
    data: offersData,
    isLoading,
    refetch,
  } = useGetMyOffers(
    { orderId: order.id!, userId: auth.id! },
    {
      query: {
        refetchInterval: 1 * 60 * 1000,
      },
    },
  );

  const { mutateAsync: mutateSaveMyOffersAsync, isPending: isSaveMyOffersPending } =
    useSaveMyOffers();

  const isResponseSign = offersData?.data.payload?.responseSign;
  const allowRetradingRound = offersData?.data.payload?.allowRetradingRound;

  // Преобразуем данные из API в формат для формы, сортируем по indexNumber
  const tableData = useMemo(() => {
    if (!offersData?.data?.payload) return [];

    const directions = offersData.data.payload.directions ?? [];

    return directions
      .map((direction) => ({
        id: String(direction.index ?? ""),
        indexNumber: direction.index ?? 0,
        roadStation: getStationName(direction.unloadingRoad, direction.unloadingStation) ?? "-",
        unit: direction.tariffRoad ? TARIFF_LABELS[direction.tariffRoad] : "-",
        tariffRoad: direction.tariffRoad as UnloadingDirectionDTOTariffRoad | undefined,
      }))
      .sort((a, b) => a.indexNumber - b.indexNumber);
  }, [offersData]);

  // Формируем данные по этапам (stages)
  const stagesData = useMemo(() => {
    if (!offersData?.data?.payload?.stages) return [];

    return offersData.data.payload.stages
      .map((stage) => ({
        stage: stage.stage ?? 0,
        ownerOffers: stage.ownerOffers ?? [],
        sobOffers: stage.sobOffers ?? [],
      }))
      .sort((a, b) => a.stage - b.stage);
  }, [offersData]);

  // Функция для получения значения ownerOffers по indexNumber и stage
  const getOwnerOfferValue = (indexNumber: number, stage: number): number | null => {
    const stageData = stagesData.find((s) => s.stage === stage);
    return stageData?.ownerOffers.find((o) => o.indexNumber === indexNumber)?.wagonTariff ?? null;
  };

  // Функция для получения значения sobOffers по indexNumber и stage
  const getSobOfferValue = (indexNumber: number, stage: number): number | null => {
    const stageData = stagesData.find((s) => s.stage === stage);
    return stageData?.sobOffers.find((o) => o.indexNumber === indexNumber)?.wagonTariff ?? null;
  };

  const totalWagons = useMemo(() => offersData?.data?.payload?.totalWagons ?? 0, [offersData]);
  const countWagonsOwner = useMemo(
    () => offersData?.data?.payload?.countWagonsOwner ?? 0,
    [offersData],
  );

  const handleSubmit = (data: SobProposalFormValues) => {
    const offers = Object.entries(data.proposals ?? {})
      .map(([index, value]) => ({
        indexNumber: Number(index),
        wagonTariff: value ? Number(value) : undefined,
      }))
      .filter((offer) => offer.wagonTariff !== undefined);

    mutateSaveMyOffersAsync({
      data: {
        orderId: order.id!,
        userId: auth.id!,
        offers,
      },
    })
      .then(() => refetch())
      .then(() => {
        toast({
          title: "Ответ отправлен",
        });
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка",
          description: error instanceof Error ? error.message : "Не удалось отправить ответ",
        });
      });
  };

  useEffect(() => {
    if (tableData.length && stagesData.length) {
      const lastStage = stagesData[stagesData.length - 1];

      const proposals = tableData.reduce(
        (acc, row) => ({
          ...acc,
          [row.id]: lastStage?.sobOffers.find((o) => o.indexNumber === row.indexNumber)
            ?.wagonTariff,
        }),
        {} as Record<string, number | undefined>,
      );

      const isKeepValues = !lastStage.sobOffers.length;

      const tariffRoad = tableData.reduce(
        (acc, row) => ({
          ...acc,
          [row.id]: row.tariffRoad,
        }),
        {} as Record<string, UnloadingDirectionDTOTariffRoad | undefined>,
      );

      form.reset(
        {
          proposals,
          tariffRoad,
        },
        {
          keepErrors: true,
          keepValues: isKeepValues,
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, stagesData]);

  if (isLoading) {
    return (
      <div className="mb-6">
        <p className="text-Text-secondary">Загрузка данных...</p>
      </div>
    );
  }

  if (!offersData?.data?.payload || !tableData.length) {
    return null;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
        <div className="mb-5 md:mb-6 overflow-hidden">
          <div className="p-3 md:p-4">
            <h2 className="text-2xl font-semibold text-Text-main leading-7">Предложение</h2>

            {stagesData.length > 1 && (
              <p className="text-base md:text-lg text-Text-secondary mt-3 md:mt-4">
                Переторжка №{stagesData.length - 1}
              </p>
            )}
          </div>

          <Table className="min-w-[600px] md:min-w-full mb-3 md:mb-0">
            <TableHeader>
              <TableRow>
                <StyledTableHead>Дорога/Станция выгрузки</StyledTableHead>

                {/* скрываем stage 0 тк он всегда пустой для ГВ */}
                {stagesData.map(({ stage }) => (
                  <StyledTableHead key={`owner-${stage}`} className={stage === 0 ? "hidden" : ""}>
                    {stage === 1
                      ? "Стартовое предложение Грузовладельца"
                      : `Предложение Грузовладельца по переторжке ${stage}`}
                  </StyledTableHead>
                ))}

                {stagesData.map(({ stage }) => (
                  <StyledTableHead key={`sob-${stage}`}>
                    {stage === 0
                      ? "Ваше стартовое предложение"
                      : `Ваше предложение по переторжке ${stage}`}
                  </StyledTableHead>
                ))}

                <StyledTableHead>Тип тарифа</StyledTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tableData.map((row) => {
                const indexNumber = row.indexNumber;

                return (
                  <TableRow key={row.id}>
                    <StyledTableCell>{row.roadStation}</StyledTableCell>

                    {/* начинаем со stage 1 для ГВ */}
                    {stagesData
                      .filter(({ stage }) => stage > 0)
                      .map(({ stage }) => (
                        <StyledTableCell key={`owner-${row.id}-${stage}`}>
                          {getOwnerOfferValue(indexNumber, stage) ?? "-"}
                        </StyledTableCell>
                      ))}

                    {stagesData.map(({ stage }, index, list) => {
                      return index === list.length - 1 ? (
                        <StyledTableCell key={`sob-${row.id}-${stage}`}>
                          <FormField
                            control={form.control}
                            name={`proposals.${row.id}`}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Input
                                    {...field}
                                    size="sm"
                                    type="number"
                                    className="w-full max-w-[120px]"
                                    disabled={isResponseSign || !allowRetradingRound}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <input
                            type="hidden"
                            {...form.register(`tariffRoad.${row.id}`)}
                            value={row.tariffRoad || ""}
                            disabled
                          />
                        </StyledTableCell>
                      ) : (
                        <StyledTableCell key={`sob-${row.id}-${stage}`}>
                          {getSobOfferValue(indexNumber, stage) ?? "-"}
                        </StyledTableCell>
                      );
                    })}

                    <StyledTableCell>{row.unit}</StyledTableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <StyledTableCell>
                  <b>Итого вагонов</b>
                </StyledTableCell>

                {/* скрываем stage 0 тк он всегда пустой для ГВ */}
                {stagesData.map(({ stage }) => (
                  <StyledTableCell
                    key={`owner-total-${stage}`}
                    className={stage === 0 ? "hidden" : ""}
                  >
                    {stage === 1 ? <b>{totalWagons}</b> : null}
                  </StyledTableCell>
                ))}

                {stagesData.map(({ stage }) => (
                  <StyledTableCell key={`sob-total-${stage}`}>
                    {stage === 0 ? <b>{countWagonsOwner}</b> : null}
                  </StyledTableCell>
                ))}

                <StyledTableCell>
                  <b>шт.</b>
                </StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <div className="flex justify-end mb-6">
          <Button
            type="submit"
            disabled={isSaveMyOffersPending || isResponseSign || !allowRetradingRound}
            className="bg-Main-orange hover:bg-Main-orange/90 text-white rounded-md px-6 py-2 w-full md:w-auto"
          >
            {isSaveMyOffersPending ? "Отправка..." : "Отправить предложение по переторжке"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
